/**
 * Accesses AI — Frontend ローカル Contract（Shadow AI / Dormant AI）
 *
 * backend PR #7705 で `@ITForceOrpcController('AccessesAi')` として実装された 4 エンドポイントを、
 * `@standandforce/api-contract@0.0.1062` の publish 前の暫定としてローカルに再現する。
 * スキーマは `make gen-api` 適用済みの `schema.d.ts`（`operations["accessesAi.*"]`）と一致させてある。
 *
 * TODO(F-1): `@standandforce/api-contract@0.0.1062` 以降が install 可能になったら本ファイルを削除し、
 *   client.ts の import を `@standandforce/api-contract` の `accessesAiContract` に切り替える
 *   （min-release-age ポリシーにより現在 install 不可）。エクスポート名は実パッケージと一致させてあるため、
 *   import 元の変更のみで済む想定。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/** Shadow AI 一覧の並び替えキー（schema.d.ts: accessesAi.shadowServices.orderBy と一致） */
const ShadowAiOrderByEnum = z.enum(['IT_SERVICE_NAME', 'CATEGORY', 'ACCESS_COUNT', 'UNIQUE_USER_COUNT', 'ACCESS_COUNT_7DAYS', 'ACCESS_COUNT_30DAYS']);
const ShadowAiAccessPersonSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string()
});
const ShadowAiTraceableAppSchema = z.object({
  id: z.string(),
  name: z.string(),
  categories: z.array(z.string()),
  websiteUrl: z.string(),
  integratableAppId: z.string().nullable(),
  avatarUrl: z.string()
});
export const ShadowAiServiceSchema = z.object({
  id: z.string(),
  traceableApp: ShadowAiTraceableAppSchema,
  accessPersons: z.array(ShadowAiAccessPersonSchema),
  violationType: z.string(),
  accessCount7Days: z.number(),
  accessCount30Days: z.number(),
  uniqueUserCount: z.number()
});
export const ListShadowAiServicesQuerySchema = paginationQuerySchema(ShadowAiOrderByEnum).extend({
  freeText: z.string().max(200).optional()
});
const AiAlertSummarySchema = z.object({
  currentMonthShadowAiCount: z.number(),
  shadowAiCountChange: z.number().nullable()
});
const GetAiAlertSummaryQuerySchema = z.object({
  year: z.number().optional(),
  month: z.number().optional()
});

/** Dormant AI 一覧の並び替えキー（schema.d.ts: accessesAi.dormantSoftware.orderBy と一致） */
const DormantAiOrderByEnum = z.enum(['SOFTWARE_NAME', 'ACTIVE_USER_COUNT', 'DORMANT_COUNT', 'DORMANT_RATE', 'LAST_CALCULATED_AT']);
export const DormantAiSoftwareSchema = z.object({
  softwareId: z.string(),
  softwareName: z.string(),
  categories: z.array(z.string()),
  activeUserCount: z.number(),
  dormantCount: z.number(),
  dormantRate: z.number(),
  // 実 wire は ISO 文字列（schema.d.ts: `string | null`）。既存ローカル contract の日付慣習に合わせ z.string()。
  // simpleDateColumn が string | Date | null を両対応するため、F-1 で実パッケージが z.date() を採っても表示は不変。
  lastCalculatedAt: z.string().nullable(),
  logoUrl: z.string().nullable(),
  websiteUrl: z.string().nullable()
});
export const ListDormantAiSoftwareQuerySchema = paginationQuerySchema(DormantAiOrderByEnum).extend({
  freeText: z.string().max(200).optional()
});
const DormantAiSummarySchema = z.object({
  dormantAccountCount: z.number(),
  affectedSoftwareCount: z.number(),
  thresholdMonths: z.number(),
  isDefaultThreshold: z.boolean(),
  previousMonthDormantAccountCount: z.number(),
  dormantAccountCountChange: z.number()
});
const GetDormantAiSummaryQuerySchema = z.object({});
export const accessesAiContract = oc.router({
  shadowServices: oc.route({
    method: 'GET',
    path: '/v2/accesses/ai/shadow-services'
  }).input(ListShadowAiServicesQuerySchema).output(paginatedSchema(ShadowAiServiceSchema)),
  dormantSoftware: oc.route({
    method: 'GET',
    path: '/v2/accesses/ai/dormancy/software'
  }).input(ListDormantAiSoftwareQuerySchema).output(paginatedSchema(DormantAiSoftwareSchema)),
  alertSummary: oc.route({
    method: 'GET',
    path: '/v2/accesses/ai/alerts/summary'
  }).input(GetAiAlertSummaryQuerySchema).output(AiAlertSummarySchema),
  dormantSummary: oc.route({
    method: 'GET',
    path: '/v2/accesses/ai/dormancy/summary'
  }).input(GetDormantAiSummaryQuerySchema).output(DormantAiSummarySchema)
});